import React from 'react';
import { Modal, Row, Col, Tooltip, Divider, Tabs } from 'antd';
import Text from 'antd/lib/typography/Text';
import moment from 'moment';
import {
  formatWholeNumber,
  formatNumber2SigFig,
  formatCurrency2SigFig,
} from '../../../../core/utils/campaigns.js';

const { TabPane } = Tabs;

const labelStyle = {
  fontSize: '13px',
  color: '#8c8c8c',
};

const valueStyle = {
  fontSize: '13px',
  fontWeight: 600,
  color: '#262626',
};

const sectionTitleStyle = {
  fontSize: '14px',
  fontWeight: 600,
  color: '#434343',
  marginBottom: '12px',
  display: 'block',
};

const DetailRow = ({ label, value, tooltip }) => (
  <Row
    style={{
      padding: '8px 0',
      borderBottom: '1px solid #f5f5f5',
    }}
    type="flex"
    justify="space-between"
    align="middle"
  >
    <Col>
      {tooltip ? (
        <Tooltip title={tooltip}>
          <Text style={labelStyle}>{label}</Text>
        </Tooltip>
      ) : (
        <Text style={labelStyle}>{label}</Text>
      )}
    </Col>
    <Col>
      <Text style={valueStyle}>{value}</Text>
    </Col>
  </Row>
);

const PercentageCurrent = ({ pacingData, status, switchPacingYesterday, paceAdditionalInfo }) => {
  const todayEntry = paceAdditionalInfo?.[0];
  const yesterdayEntry = paceAdditionalInfo?.[1];
  const source = switchPacingYesterday ? yesterdayEntry : todayEntry;

  let pace = source?.pace ?? 0;
  let budgetSpent = source?.budgetSpent ?? 0;
  let budgetSpentToday = source?.budgetSpentToday ?? 0;
  let budgetLeft = source?.budgetLeft ?? 0;
  let dailyAvgBudgetLeft = source?.dailyAvgBudgetLeft ?? 0;
  const totalBudget = source?.totalBudget ?? pacingData?.overallBudget ?? 0;

  if (status === 'FINISHED') {
    dailyAvgBudgetLeft = 0;
    budgetLeft = totalBudget - budgetSpent;
    budgetSpentToday = 0;
    pace = todayEntry?.pace ?? pacingData?.pace ?? 0;
  }

  const paceColor = getPaceColor(pace, true);

  return (
    <>
      <Text style={sectionTitleStyle}>Current Budget Pacing</Text>

      <DetailRow
        label="Pace"
        value={<span style={{ color: paceColor }}>{formatNumber2SigFig(pace)}%</span>}
        tooltip="Pacing for Percentage pricing model"
      />
      <DetailRow
        label="Remaining"
        value={formatCurrency2SigFig(budgetLeft)}
      />
      <DetailRow
        label="Expected Daily"
        value={formatCurrency2SigFig(dailyAvgBudgetLeft)}
        tooltip="Daily goal"
      />
      <DetailRow
        label="Spend"
        value={formatCurrency2SigFig(budgetSpentToday ?? 0)}
        tooltip="Amount spent within the day"
      />
      <DetailRow
        label="Total Budget Spent"
        value={formatCurrency2SigFig(budgetSpent)}
        tooltip="Cumulative budget spent as of the selected day"
      />
      <DetailRow
        label="Total Budget"
        value={formatCurrency2SigFig(totalBudget)}
        tooltip="Total Budget for the current pacing period"
      />
    </>
  );
};

const PercentageAllTime = ({ pacingData, row, switchPacingYesterday, paceAdditionalInfo }) => {
  const allTime = getAllTimeEntry(paceAdditionalInfo);
  const totalBudget = allTime?.totalBudget ?? pacingData?.overallBudget ?? 0;
  const totalBudgetSpent = allTime?.budgetSpent ?? pacingData?.overallSpend ?? 0;
  const remaining = allTime?.budgetLeft ?? (totalBudget - totalBudgetSpent);
  const expectedDaily =
    allTime?.dailyAvgBudget ??
    (() => {
      const flightDays = getFlightDays(row);
      return flightDays > 0 ? totalBudget / flightDays : 0;
    })();
  const pace =
    allTime?.budgetPace ?? (totalBudget > 0 ? (totalBudgetSpent / totalBudget) * 100 : 0);
  const spentToday = switchPacingYesterday
    ? pacingData?.budgetSpentYesterday || 0
    : pacingData?.budgetSpentToday || 0;
  const paceColor = getPaceColor(pace, true);

  return (
    <>
      <Text style={sectionTitleStyle}>All Time Budget Pacing</Text>

      <DetailRow
        label="Pace"
        value={<span style={{ color: paceColor }}>{formatNumber2SigFig(pace)}%</span>}
        tooltip="Lifetime pace across the full flight"
      />
      <DetailRow
        label="Remaining"
        value={formatCurrency2SigFig(remaining)}
        tooltip="Total Budget minus Total Budget Spent"
      />
      <DetailRow
        label="Expected Daily"
        value={formatCurrency2SigFig(expectedDaily)}
        tooltip="Total Budget divided by total flight days"
      />
      <DetailRow
        label={switchPacingYesterday ? 'Spent Yesterday' : 'Spent Today'}
        value={formatCurrency2SigFig(spentToday)}
        tooltip="Amount spent within the day"
      />
      <DetailRow
        label="Budget Spent"
        value={formatCurrency2SigFig(totalBudgetSpent)}
        tooltip="Total amount spent from the start of the campaign"
      />
      <DetailRow
        label="Total Budget"
        value={formatCurrency2SigFig(totalBudget)}
        tooltip="Total Budget for the whole duration of the campaign"
      />
    </>
  );
};

const ImpressionCurrent = ({ pacingData, row, status, switchPacingYesterday, paceAdditionalInfo }) => {
  const todayEntry = paceAdditionalInfo?.[0];
  const yesterdayEntry = paceAdditionalInfo?.[1];
  const source = switchPacingYesterday ? yesterdayEntry : todayEntry;

  let pace = source?.pace ?? 0;
  let impressionsLeft = source?.impressionsLeft ?? 0;
  let dailyImpressionsGoal = source?.dailyAvgImpressionsLeft ?? 0;
  let todaysImpressions = source?.impressionServedToday ?? 0;
  let impressionsSpent = source?.impressionsServed ?? 0;
  const impressionGoal = source?.impressionGoal ?? pacingData?.impressionGoal ?? 0;

  if (status === 'FINISHED') {
    impressionsSpent = row?.aggregateData?.overallData?.aggImpressions || impressionsSpent;
    dailyImpressionsGoal = 0;
    pace = todayEntry?.pace ?? pacingData?.pace ?? 0;
    todaysImpressions = 0;
    impressionsLeft = impressionGoal - impressionsSpent;
  }

  const paceColor = getPaceColor(pace, false);

  return (
    <>
      <Text style={sectionTitleStyle}>Current Impression Pacing</Text>

      <DetailRow
        label="Pace"
        value={<span style={{ color: paceColor }}>{pace !== null ? formatNumber2SigFig(pace) : 0}%</span>}
        tooltip={`Projected Pace. (Impression from ${switchPacingYesterday ? 'Yesterday' : 'Today'} * Days Remaining) + Served Impressions / Impression Goal`}
      />
      <DetailRow
        label={impressionsLeft < 0 ? 'Overage' : 'Remaining'}
        value={formatWholeNumber(impressionsLeft)}
      />
      {status !== 'FINISHED' && (
        <DetailRow
          label="Daily Goal"
          value={dailyImpressionsGoal ? formatWholeNumber(dailyImpressionsGoal) : '0'}
          tooltip="(Impression Goal - Served Impressions) / Days Remaining"
        />
      )}
      {status !== 'FINISHED' && (
        <DetailRow
          label="Impressions"
          value={todaysImpressions ? formatWholeNumber(todaysImpressions) : '0'}
          tooltip="Number of impressions served within the day"
        />
      )}
      <DetailRow
        label="Total Served Impressions"
        value={impressionsSpent ? formatWholeNumber(impressionsSpent) : '0'}
        tooltip="Cumulative impressions served as of the selected day"
      />
      <DetailRow
        label="Impression Goal"
        value={impressionGoal ? formatWholeNumber(impressionGoal) : '0'}
        tooltip="Impression goal for the current pacing period"
      />
    </>
  );
};

const ImpressionAllTime = ({ pacingData, row, switchPacingYesterday, paceAdditionalInfo }) => {
  const allTime = getAllTimeEntry(paceAdditionalInfo);
  const overallImpressions =
    allTime?.totalImpressionGoal ?? pacingData?.overallImpressions ?? 0;
  const overallDeliveredImpressions =
    allTime?.impressionsServed ?? pacingData?.overallDeliveredImressions ?? 0;
  const remaining = allTime?.impressionsLeft ?? (overallImpressions - overallDeliveredImpressions);
  const dailyGoal =
    allTime?.dailyAvgImpressions ??
    (() => {
      const flightDays = getFlightDays(row);
      return flightDays > 0 ? overallImpressions / flightDays : 0;
    })();
  const pace =
    allTime?.impressionPace ??
    (overallImpressions > 0 ? (overallDeliveredImpressions / overallImpressions) * 100 : 0);
  const todaysImpressions = switchPacingYesterday
    ? pacingData?.impressionsYesterday || 0
    : paceAdditionalInfo?.[0]?.impressionServedToday || 0;
  const paceColor = getPaceColor(pace, false);

  return (
    <>
      <Text style={sectionTitleStyle}>All Time Impression Pacing</Text>

      <DetailRow
        label="Pace"
        value={<span style={{ color: paceColor }}>{formatNumber2SigFig(pace)}%</span>}
        tooltip="Lifetime pace across the full flight"
      />
      <DetailRow
        label={remaining < 0 ? 'Overage' : 'Remaining'}
        value={formatWholeNumber(remaining)}
        tooltip="Impression Goal minus Total Delivered"
      />
      <DetailRow
        label="Daily Goal"
        value={dailyGoal ? formatWholeNumber(dailyGoal) : '0'}
        tooltip="Impression Goal divided by total flight days"
      />
      <DetailRow
        label={switchPacingYesterday ? 'Yesterday' : 'Today'}
        value={todaysImpressions ? formatWholeNumber(todaysImpressions) : '0'}
        tooltip="Number of impressions served within the day"
      />
      <DetailRow
        label="Served Impressions"
        value={overallDeliveredImpressions ? formatWholeNumber(overallDeliveredImpressions) : '0'}
        tooltip="Number of impressions served from the start of the campaign"
      />
      <DetailRow
        label="Impression Goal"
        value={overallImpressions ? formatWholeNumber(overallImpressions) : '0'}
        tooltip="Total Impression goal for the whole duration of the campaign"
      />
    </>
  );
};

function getAllTimeEntry(paceAdditionalInfo) {
  if (!Array.isArray(paceAdditionalInfo)) return null;
  return paceAdditionalInfo.find((e) => e?.identifier === 'allTime') || null;
}

function getFlightDays(row) {
  if (!row?.startDate || !row?.endDate) return 0;
  const start = moment(row.startDate);
  const end = moment(row.endDate);
  if (!start.isValid() || !end.isValid()) return 0;
  return Math.max(0, end.startOf('day').diff(start.startOf('day'), 'days'));
}

const formatDateOnly = (value) => {
  if (!value) return '—';
  const m = moment.utc(value);
  return m.isValid() ? m.format('YYYY-MM-DD') : '—';
};

const ScheduleSection = ({ entry, pacingData, row }) => {
  const startDate = formatDateOnly(entry?.startDate ?? row?.startDate);
  const endDate = formatDateOnly(entry?.endDate ?? row?.endDate);
  const daysIntoFlight = entry?.daysIntoFlight ?? pacingData?.daysIntoFlight ?? '—';
  const daysLeft = entry?.daysLeft ?? pacingData?.daysLeft ?? '—';
  const dataRecorded = formatDateOnly(pacingData?.lastDataDate);

  return (
    <>
      <Divider style={{ margin: '12px 0' }} />
      <Text style={sectionTitleStyle}>Schedule</Text>

      <DetailRow
        label="Start Date"
        value={startDate}
        tooltip="Campaign flight start date"
      />
      <DetailRow
        label="End Date"
        value={endDate}
        tooltip="Campaign flight end date"
      />
      <DetailRow
        label="Days Into Flight"
        value={daysIntoFlight}
        tooltip="Days elapsed since the campaign start date"
      />
      <DetailRow
        label="Days Left"
        value={daysLeft}
        tooltip="Days remaining based on campaign end date"
      />
      <DetailRow
        label="Data Recorded"
        value={dataRecorded}
        tooltip="Date of the most recently recorded pacing data"
      />
    </>
  );
};

const PacingDetailModal = ({
  visible,
  onClose,
  data,
}) => {
  const { pacingData, row, status, isPercentage, switchPacingYesterday, paceAdditionalInfo } = data || {};
  const currentEntry = switchPacingYesterday ? paceAdditionalInfo?.[1] : paceAdditionalInfo?.[0];
  const allTimeEntry = getAllTimeEntry(paceAdditionalInfo);

  return (
    <Modal
      title={`Pacing Details — ${row?.name || 'Campaign'}`}
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={480}
    >
      <Tabs defaultActiveKey="current">
        <TabPane
          tab={`Current Pacing (${switchPacingYesterday ? 'Yesterday' : 'Today'})`}
          key="current"
        >
          {isPercentage ? (
            <PercentageCurrent
              pacingData={pacingData}
              status={status}
              switchPacingYesterday={switchPacingYesterday}
              paceAdditionalInfo={paceAdditionalInfo}
            />
          ) : (
            <ImpressionCurrent
              pacingData={pacingData}
              row={row}
              status={status}
              switchPacingYesterday={switchPacingYesterday}
              paceAdditionalInfo={paceAdditionalInfo}
            />
          )}
          <ScheduleSection entry={currentEntry} pacingData={pacingData} row={row} />
        </TabPane>
        <TabPane tab="All Time Pacing" key="allTime">
          {isPercentage ? (
            <PercentageAllTime
              pacingData={pacingData}
              row={row}
              switchPacingYesterday={switchPacingYesterday}
              paceAdditionalInfo={paceAdditionalInfo}
            />
          ) : (
            <ImpressionAllTime
              pacingData={pacingData}
              row={row}
              switchPacingYesterday={switchPacingYesterday}
              paceAdditionalInfo={paceAdditionalInfo}
            />
          )}
          <ScheduleSection entry={allTimeEntry} pacingData={pacingData} row={row} />
        </TabPane>
      </Tabs>
    </Modal>
  );
};

function getPaceColor(pace, isPercentage) {
  if (!pace) return '#ff4d4f';
  if (isPercentage) {
    if (pace <= 100 && pace >= 50) return '#52c41a';
    if (pace >= 0 && pace < 50) return '#faad14';
    return '#ff4d4f';
  }
  if (pace >= 95) return '#52c41a';
  if (pace >= 91 && pace <= 95) return '#faad14';
  return '#ff4d4f';
}

export default PacingDetailModal;
